import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { getPOIColorToken, getPOISymbol } from '@inloco/shared';
import type { TranslatedPOI } from '@/types/poi';
import { ICONS_STYLE } from '@/utils/constants';


interface UsePOIMarkersProps {
    mapRef: React.RefObject<L.Map | null>;
    translatedPOIs: TranslatedPOI[];
    onPOIClick?: (poi: TranslatedPOI) => void;
}

export function usePOIMarkers({
    mapRef,
    translatedPOIs,
    onPOIClick
}: UsePOIMarkersProps) {
    const poiMarkersRef = useRef<Map<string, L.Marker>>(new Map());
    const onPOIClickRef = useRef(onPOIClick);
    const { family, weight } = ICONS_STYLE; // Assuming ICONS_STYLE in future is defined on configuration and not hardcoded in the component

    useEffect(() => {
        onPOIClickRef.current = onPOIClick;
    }, [onPOIClick]);

    // Cleanup: clean refs when map is destroyed
    useEffect(() => {
        const markers = poiMarkersRef.current;
        return () => {
            markers.clear();
        };
    }, []);

    // POI markers
    useEffect(() => {
        if (!mapRef.current || translatedPOIs.length === 0) return;

        const currentMarkers = poiMarkersRef.current;
        const currentIds = new Set(translatedPOIs.map(p => p.id));

        // Remove POI markers no longer present
        currentMarkers.forEach((marker, id) => {
            if (!currentIds.has(id)) {
                marker.remove();
                currentMarkers.delete(id);
            }
        });

        // Add/update POI markers
        translatedPOIs.forEach(poi => {
            const { id, position, type } = poi;

            const existingMarker = currentMarkers.get(id);

            const { cssVar, fallback } = getPOIColorToken(type);

            const hasEmergencyLevel = poi.type === 'danger' && poi.meta?.emergencyLevel === 'High';

            const iconUrl = `/icons/svg/${family}/${weight}/${getPOISymbol(type)}.svg`;

            const poiIcon = L.divIcon({
                className: 'poi-marker',
                html: `
                    <div class="poi-icon-circle${hasEmergencyLevel ? ' poi-icon-circle--emergency' : ''}" style="--poi-color: var(${cssVar}, ${fallback});">
                        <div 
                            class="poi-icon-symbol" 
                            style="
                                -webkit-mask: url('${iconUrl}') no-repeat center / contain;
                                mask: url('${iconUrl}') no-repeat center / contain;
                            "
                            ></div>
                    </div>
                `,
                iconSize: [32, 32],
                iconAnchor: [16, 16],
            });

            if (existingMarker) {
                existingMarker.setLatLng([position.lt, position.lg]);
                existingMarker.setIcon(poiIcon);
            } else {
                // Create new marker
                const marker = L.marker([position.lt, position.lg], {
                    icon: poiIcon,
                }).addTo(mapRef.current!);

                // Add click event
                marker.on('click', () => {
                    if (onPOIClickRef.current) {
                        onPOIClickRef.current(poi);
                    }
                })

                //marker.bindPopup(popupContent);
                currentMarkers.set(id, marker);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translatedPOIs]);
}
